import type {
  Collaborator,
  CollaboratorState,
  Filter,
  Folder,
  Item,
  Label,
  Note,
  Project,
  ProjectViewOptionsDefaults,
  Reminder,
  Section,
  SyncResponse,
  User,
  ViewOption,
  Workspace,
} from "../api/types";

export type ById<T> = Readonly<Record<string, T>>;

/** Local mirror of the Sync resources. Deleted rows are removed; completed items are kept (checked). */
export interface SyncState {
  syncToken: string;
  user: User | null;
  projects: ById<Project>;
  sections: ById<Section>;
  items: ById<Item>;
  notes: ById<Note>;
  projectNotes: ById<Note>;
  labels: ById<Label>;
  filters: ById<Filter>;
  reminders: ById<Reminder>;
  collaborators: ById<Collaborator>;
  /** keyed `${project_id}:${user_id}` */
  collaboratorStates: ById<CollaboratorState>;
  /** keyed `${view_type}:${object_id ?? ""}` */
  viewOptions: ById<ViewOption>;
  /** keyed project_id */
  pvods: ById<ProjectViewOptionsDefaults>;
  workspaces: ById<Workspace>;
  folders: ById<Folder>;
}

export const emptyState = (): SyncState => ({
  syncToken: "*",
  user: null,
  projects: {},
  sections: {},
  items: {},
  notes: {},
  projectNotes: {},
  labels: {},
  filters: {},
  reminders: {},
  collaborators: {},
  collaboratorStates: {},
  viewOptions: {},
  pvods: {},
  workspaces: {},
  folders: {},
});

export const viewOptionKey = (viewType: string, objectId?: string | null) => `${viewType}:${objectId ?? ""}`;

/** Upsert rows into a map; rows flagged `is_deleted` (tombstones) are removed. */
export function mergeRows<T extends object>(
  base: ById<T>,
  rows: readonly T[] | undefined,
  key: (row: T) => string,
  replace: boolean,
  isGone: (row: T) => boolean = (r) => (r as { is_deleted?: boolean }).is_deleted === true,
): ById<T> {
  if (!rows) return replace ? {} : base;
  const next: Record<string, T> = replace ? {} : { ...base };
  for (const row of rows) {
    const k = key(row);
    if (isGone(row)) delete next[k];
    else next[k] = row;
  }
  return next;
}

const byId = <T extends { id: string }>(r: T) => r.id;

function workspaceRows(w: SyncResponse["workspaces"]): Workspace[] | undefined {
  if (!w) return undefined;
  return Array.isArray(w) ? w : Object.values(w);
}

/** Apply a Sync read response (full or incremental) to a state. */
export function mergeSyncResponse(state: SyncState, res: SyncResponse): SyncState {
  const full = res.full_sync === true;
  return {
    syncToken: res.sync_token ?? state.syncToken,
    user: res.user ? { ...(full ? {} : state.user), ...res.user } : state.user,
    // Archived projects leave the sidebar and their content leaves sync, like deleted ones.
    projects: mergeRows(state.projects, res.projects, byId, full, (p) => p.is_deleted || p.is_archived),
    sections: mergeRows(state.sections, res.sections, byId, full, (s) => s.is_deleted || s.is_archived),
    items: mergeRows(state.items, res.items, byId, full),
    notes: mergeRows(state.notes, res.notes, byId, full),
    projectNotes: mergeRows(state.projectNotes, res.project_notes, byId, full),
    labels: mergeRows(state.labels, res.labels, byId, full),
    filters: mergeRows(state.filters, res.filters, byId, full),
    reminders: mergeRows(state.reminders, res.reminders, byId, full),
    collaborators: mergeRows(state.collaborators, res.collaborators, byId, full),
    collaboratorStates: mergeRows(
      state.collaboratorStates,
      res.collaborator_states,
      (c) => `${c.project_id}:${c.user_id}`,
      full,
      (c) => c.is_deleted === true || c.state === "deleted",
    ),
    viewOptions: mergeRows(state.viewOptions, res.view_options, (v) => viewOptionKey(v.view_type, v.object_id), full),
    pvods: mergeRows(state.pvods, res.project_view_options_defaults, (p) => p.project_id, full),
    workspaces: mergeRows(state.workspaces, workspaceRows(res.workspaces), byId, full),
    folders: mergeRows(state.folders, res.folders, byId, full),
  };
}
